import React, { useContext, useEffect, useState, useCallback } from "react";
import { useLocalStorage } from "@rehooks/local-storage";
import { useAuth0 } from "./react-auth0-spa";
import config from "./api_config.json";

export const AppContext = React.createContext();
export const useAppContext = () => useContext(AppContext);

export const AppProvider = (props) => {
  const isMobile = window.innerWidth <= 768;
  const defaultValue = isMobile ? true : false;
  
  const [isProd, setIsProd] = useLocalStorage("isProd", defaultValue);
  const { getTokenSilently, isAuthenticated, loading, loginWithRedirect } = useAuth0();

  // Assumed org state (server authoritative)
  const [assumedOrg, setAssumedOrg] = useState(null);
  const [assumedOrgLoading, setAssumedOrgLoading] = useState(false);

  // Unified assumed org state (Eterno + FuneralCloud)
  const [unifiedAssumedOrg, setUnifiedAssumedOrg] = useState(null);

  const toggleEnv = () => {
    setIsProd(!isProd);
  };

  const refreshAssumedOrg = useCallback(async () => {
    // Only attempt when authenticated and not in loading state
    if (loading || !isAuthenticated) {
      setAssumedOrg(null);
      return;
    }
    try {
      setAssumedOrgLoading(true);
      const token = await getTokenSilently();
      const response = await fetch(
        `${config.baseUrl[isProd ? "prod" : "dev"]}/assume-org-role`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        setAssumedOrg(null);
        return;
      }
      const json = await response.json();
      const server = json?.data;
      if (server?.orgId) {
        setAssumedOrg({ id: server.orgId, name: server.orgName || "", at: Date.now() });
      } else {
        setAssumedOrg(null);
      }
    } catch (_) {
      setAssumedOrg(null);
    } finally {
      setAssumedOrgLoading(false);
    }
  }, [getTokenSilently, isAuthenticated, loading, isProd]);

  const refreshUnifiedAssumedOrg = useCallback(async () => {
    if (loading || !isAuthenticated) {
      setUnifiedAssumedOrg(null);
      return;
    }
    try {
      const token = await getTokenSilently();
      const response = await fetch(
        `${config.baseUrl[isProd ? "prod" : "dev"]}/assume-org-role/unified`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        setUnifiedAssumedOrg(null);
        return;
      }
      const json = await response.json();
      setUnifiedAssumedOrg(json);
    } catch (_) {
      setUnifiedAssumedOrg(null);
    }
  }, [getTokenSilently, isAuthenticated, loading, isProd]);

  const assumeUnifiedOrg = useCallback(async ({ orgId, marketplaceOrgId } = {}) => {
    if (loading) return null;
    if (!isAuthenticated) {
      await loginWithRedirect();
      return null;
    }
    const token = await getTokenSilently();
    const response = await fetch(
      `${config.baseUrl[isProd ? "prod" : "dev"]}/assume-org-role/unified`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orgId, marketplaceOrgId }),
      }
    );
    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(errorBody || `HTTP ${response.status}`);
    }
    const json = await response.json();
    setUnifiedAssumedOrg(json);
    await refreshAssumedOrg();
    return json;
  }, [getTokenSilently, isAuthenticated, loading, loginWithRedirect, isProd, refreshAssumedOrg]);

  const DEMO_ORG_ID = "PNgeKjwe16";

  const assumeOrg = useCallback(async (orgId) => {
    if (loading) return;
    if (!isAuthenticated) {
      await loginWithRedirect();
      return;
    }
    // Use unified endpoint to keep both products in sync
    await assumeUnifiedOrg({ orgId });
  }, [loading, isAuthenticated, loginWithRedirect, assumeUnifiedOrg]);

  const disconnectAssumedOrg = useCallback(async () => {
    if (loading) return;
    if (!isAuthenticated) {
      await loginWithRedirect();
      return;
    }
    // Disconnect resets Eterno to demo org and clears FC
    await assumeUnifiedOrg({ orgId: DEMO_ORG_ID });
  }, [loading, isAuthenticated, loginWithRedirect, assumeUnifiedOrg]);

  useEffect(() => {
    // Seed state from server when ready
    if (!loading && isAuthenticated) {
      refreshAssumedOrg();
    } else if (!isAuthenticated) {
      setAssumedOrg(null);
    }
    // Light cross-tab: refresh on focus
    const onFocus = () => refreshAssumedOrg();
    window.addEventListener("focus", onFocus);
    // Backwards compat: refresh on custom events (if fired by legacy callers)
    const onCustom = () => refreshAssumedOrg();
    window.addEventListener("mc-assumed-org-changed", onCustom);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("mc-assumed-org-changed", onCustom);
    };
  }, [loading, isAuthenticated, refreshAssumedOrg]);

  return (
    <AppContext.Provider
      value={{
        isProd,
        toggleEnv,
        assumedOrg,
        assumedOrgLoading,
        refreshAssumedOrg,
        assumeOrg,
        disconnectAssumedOrg,
        unifiedAssumedOrg,
        assumeUnifiedOrg,
        refreshUnifiedAssumedOrg,
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
};
